
import Card from 'primevue/card';
import ProgressSpinner from 'primevue/progressspinner';
import FileUpload from 'primevue/fileupload';
import Button from 'primevue/button';
import Message from 'primevue/message';
import Toast from 'primevue/toast';

import BankDetail from '../card/BankDetail.vue';

import { computed, defineComponent, onMounted, ref, watch } from 'vue';
import { useAuthStore } from '../../stores/auth';
import { storeToRefs } from 'pinia';
import { BankDepositResult } from '../../types/deposit_withdraw';
import { useWebStore } from '../../stores/web';
import { useToast } from 'primevue/usetoast';
import { main_wallet } from '../../apis/main_wallet';
import { showVuePopup } from '../../utils/showVuePopup';
import WalletUpdate from '../../popup/WalletUpdate.vue';
import { useSession } from '../../stores/session';
import { depositWithdraw } from '../../apis/deposit_withdraw';
import { compressImageToDataURL } from '../../utils/image';

export default defineComponent({
	name: 'DepositAutoSlipNew',
	components: {
		BankDetail,
		Button, Card, Message, ProgressSpinner, FileUpload, Toast,
	},
	setup() {
		const auth = useAuthStore();
		auth.hydrate();
		const { profile } = storeToRefs(auth);

		const web = useWebStore();
		web.hydrate();
		const { webPermission } = storeToRefs(web);

		const toast = useToast();
		const session = useSession();

		const view = ref<'form' | 'success'>('form');

		const resetKey = ref(0);

		const fileInputRef = ref<HTMLInputElement | null>(null);
		const isDragging = ref(false);

		const isActive = computed<boolean>(() => webPermission.value?.isDeposit?.isAutoSlip ?? false);

		const isBankUpdate = computed<boolean>(() => {
			const hasAcc = !!profile.value?.customerBankAccount;
			const hasName = !!profile.value?.customerBankName;
			return !(hasAcc && hasName);
		});

		const msgErr = ref('');

		const form = ref({
			imgSlip: '',
			amount: 0,
			dateTime: '',
			type: 1,
			btn: { disabled: false, loading: false },
		});

		const resetForm = () => {
			msgErr.value = '';
			form.value = {
				imgSlip: '',
				amount: 0,
				dateTime: '',
				type: 1,
				btn: { disabled: false, loading: false },
			};
			if (fileInputRef.value) fileInputRef.value.value = '';
		};

		const bankAccount = ref({
			data: {
				fullName: `${profile.value?.firstName ?? ''} ${profile.value?.lastName ?? ''}`.trim(),
				bankName: profile.value?.customerBankName ?? '',
				bankNumber: profile.value?.customerBankAccount ?? '',
				bankCode: profile.value?.customerBankName ?? '',
			},
			active: !!(profile.value?.customerBankAccount && profile.value?.customerBankName),
			loading: false,
		});

		watch(profile, (p) => {
			bankAccount.value.data.fullName = `${p?.firstName ?? ''} ${p?.lastName ?? ''}`.trim();
			bankAccount.value.data.bankName = p?.customerBankName ?? '';
			bankAccount.value.data.bankNumber = p?.customerBankAccount ?? '';
			bankAccount.value.data.bankCode = p?.customerBankName ?? '';
			bankAccount.value.active = !!(p?.customerBankAccount && p?.customerBankName);
		}, { immediate: true });

		const bankDeposit = ref({
			data: { fullName: '', bankName: '', bankNumber: '', bankCode: '' },
			active: false,
			loading: false,
		});

		const handleDeposit = async () => {
			bankDeposit.value.loading = true;
			try {
				const resp = await depositWithdraw.getBankDeposit({ accountType: 'AUTO_SLIP', limit: 1 });

				if (resp.code === '8017') {
					alert(resp.message);
					await auth.logout();
					window.location.reload();
					return;
				}
				if (resp.code !== '0') throw new Error(resp.message);

				const result = resp.result as BankDepositResult[] | undefined;

				if (!Array.isArray(result) || result.length < 1) {
					bankDeposit.value.active = false;
					throw new Error('ไม่พบบัญชีธนาคาร กรุณาติดต่อ Support');
				}

				const data = result[0];
				bankDeposit.value.data = {
					fullName: data.accountName,
					bankName: data.bankName,
					bankNumber: data.accountNumber,
					bankCode: data.accountBankName,
				};
				bankDeposit.value.active = true;
			} catch (error: any) {
				toast.add({ severity: 'error', summary: 'เกิดข้อผิดพลาดบางอย่าง', detail: error?.message || String(error), life: 3000 });
			} finally {
				bankDeposit.value.loading = false;
			}
		};

		onMounted(() => {
			view.value = 'form';
			handleDeposit();
		});

		watch(view, (value) => {
			if (value === 'form') {
				resetForm();
				handleDeposit();
				resetKey.value++;
			}
			if (value === 'success') {
				resetForm();
			}
		});

		const uploadSlip = async () => {
			const payload = {
				imgSlip: form.value.imgSlip,
				amount: form.value.amount,
				dateTime: form.value.dateTime,
				type: form.value.type,
			};

			const resp = await main_wallet.uploadSlip(payload);
			if (resp.code !== '0') throw new Error(resp.message);

			resetForm();
			view.value = 'success';
			toast.add({ severity: 'success', summary: resp.message, detail: 'อัปโหลดสำเร็จ', life: 2000 });
		};

		const processFile = async (file?: File) => {
			if (!file) {
				toast.add({ severity: 'warn', summary: 'กรุณาเลือกไฟล์ก่อน', life: 2000 });
				return;
			}

			// รับทุกชนิดรูป — compressImageToDataURL จะ re-encode เป็น jpeg ให้ก่อนส่ง
			// (สลิปบนมือถือเป็น .webp บ่อย บล็อกตรงนี้เท่ากับลูกค้าฝากเงินไม่ได้)
			if (!file.type.startsWith('image/')) {
				toast.add({ severity: 'warn', summary: 'ไฟล์ต้องเป็นรูปภาพเท่านั้น', life: 2000 });
				return;
			}
			if (file.size > 10 * 1024 * 1024) {
				toast.add({ severity: 'warn', summary: 'ไฟล์ใหญ่เกิน 10MB', life: 2000 });
				return;
			}

			form.value.btn.loading = true;

			try {
				// ย่อ + บีบก่อนเข้า base64 — สลิปจากมือถือดิบ ๆ ทำให้ payload พองเป็นสิบ MB
				form.value.imgSlip = await compressImageToDataURL(file);
				toast.add({ severity: 'info', summary: 'อ่านไฟล์สำเร็จ', life: 1500 });
			} catch {
				toast.add({ severity: 'error', summary: 'อ่านไฟล์ไม่สำเร็จ', life: 2000 });
			} finally {
				form.value.btn.loading = false;
			}
		};

		const triggerFilePick = () => fileInputRef.value?.click();

		const onFileChange = (event: Event) => {
			const input = event.target as HTMLInputElement;
			processFile(input.files?.[0]);
			input.value = '';
		};

		const clearSlip = () => {
			form.value.imgSlip = '';
			if (fileInputRef.value) fileInputRef.value.value = '';
		};

		const onDrop = (event: DragEvent) => {
			isDragging.value = false;
			processFile(event.dataTransfer?.files?.[0]);
		};

		const formSubmit = async () => {
			msgErr.value = '';
			form.value.btn.loading = true;

			if (!form.value.imgSlip) {
				msgErr.value = 'กรุณาเลือกรูปสลิปฝาก';
				form.value.btn.loading = false;
				return;
			}

			if (!bankAccount.value.active || !bankDeposit.value.active) {
				msgErr.value = 'ข้อมูลบัญชียังไม่พร้อม กรุณาลองใหม่อีกครั้ง';
				form.value.btn.loading = false;
				return;
			}

			try {
				await uploadSlip();
			} catch (error: any) {
				msgErr.value = error?.message || String(error);
				toast.add({ severity: 'error', summary: 'เกิดข้อผิดพลาดบางอย่าง', detail: msgErr.value, life: 3000 });
			} finally {
				form.value.btn.loading = false;
			}
		};

		const btnClickUpdateBank = async () => {
			session.set('navbar_primary', 'wallet');
			session.set('navbar_sub', 'wallet:update');
			await showVuePopup(WalletUpdate, { tabs: 'bank' });
		};

		const resetToForm = async () => {
			resetForm();
			await handleDeposit();
			view.value = 'form';
			resetKey.value++;
		};

		return {
			profile,
			view,
			form,
			msgErr,
			bankAccount,
			bankDeposit,
			triggerFilePick,
			onFileChange,
			clearSlip,
			onDrop,
			isDragging,
			formSubmit,
			isActive,
			isBankUpdate,
			btnClickUpdateBank,
			fileInputRef,
			resetKey,
			resetToForm,
		};
	},
});
